import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EDIT_PERMISSIONS } from 'containers/Permissions';
import useHasPermission from 'containers/Permissions/hooks/useHasPermission';
import {
  selectQuestionnaireQuestions,
  makeSelectHiddenNodes,
  makeSelectNodesWithLogic,
} from 'containers/QuestionnairePage/selectors';
import { toggleNodeVisibility } from 'containers/QuestionnairePage/actions';
import questionnaireMessages from 'containers/QuestionnairePage/messages';
import { isGroup } from 'utils/nodeTypes';

import { isQuestionUsedInLoopAndMerge } from '../../NodeSettings/settings/utils/CarryForward';
import {
  getNodeByRefIdDeep,
  hasMentionOnRefId,
  hasNodeMentions,
  hasNodesMentions,
} from '../../utils';
import messages from '../messages';
import { DIVIDER_COLORS } from '../constants';
import { isGroupNodeSupportsFeatures } from '../utils';
import { makeSelectQuestionQuotasMinimal } from '../../selectorUtils/quotasMinimal';

function useStatePropsAndActions(nodeRefId, nodeType) {
  const canUnconditionalHideNode = useHasPermission(
    EDIT_PERMISSIONS.UNCONDITIONAL_HIDE_NODE,
  );

  const dispatch = useDispatch();
  const nodes = useSelector(selectQuestionnaireQuestions);
  const hiddenNodes = useSelector(makeSelectHiddenNodes);
  const questionQuotas = useSelector(makeSelectQuestionQuotasMinimal);

  const nodesWithLogic = useSelector(makeSelectNodesWithLogic);
  const isNodeHidden = useMemo(
    () => hiddenNodes.includes(nodeRefId),
    [hiddenNodes, nodeRefId],
  );
  const hasLogic = useMemo(
    () => nodesWithLogic.includes(nodeRefId),
    [nodesWithLogic, nodeRefId],
  );
  const {
    isNodeGroup,
    hasMentions,
    hasBeenCarried,
    hasBeenUsedInLoopAndMerge,
    hasQuota,
  } = useMemo(() => {
    const node = getNodeByRefIdDeep(nodes, nodeRefId);

    if (isGroup(node)) {
      const isSupportsFeatures = isGroupNodeSupportsFeatures(node.type);
      const checkForMentionOnOtherNodes = (nodeToCheck) =>
        node.questions.some((question) =>
          hasMentionOnRefId(question.refId)(nodeToCheck),
        );
      const hasSubNodesMentions =
        isSupportsFeatures &&
        (node.questions.some((question) => hasNodeMentions(question)) ||
          hasNodesMentions(nodes, checkForMentionOnOtherNodes));
      const hasSomeLogic =
        isSupportsFeatures &&
        (nodesWithLogic.includes(nodeRefId) ||
          node.questions.some((question) =>
            nodesWithLogic.includes(question.refId),
          ));

      const hasSomeQuota =
        questionQuotas[nodeRefId]?.simpleQuotasPresent ||
        questionQuotas[nodeRefId]?.advancedQuotasPresent ||
        node.questions.some(
          (question) =>
            questionQuotas[question.refId]?.simpleQuotasPresent ||
            questionQuotas[question.refId]?.advancedQuotasPresent,
        );

      return {
        hasQuota: hasSomeQuota,
        hasLogic: hasSomeLogic,
        hasMentions: hasSubNodesMentions,
        isNodeGroup: true,
        hasBeenCarried: false,
        hasBeenUsedInLoopAndMerge: false,
      };
    }

    const checkForMentionOnOtherNodes = hasMentionOnRefId(nodeRefId);

    return {
      hasQuota:
        questionQuotas[nodeRefId].simpleQuotasPresent ||
        questionQuotas[nodeRefId].advancedQuotasPresent,
      hasLogic: nodesWithLogic.includes(nodeRefId),
      hasMentions:
        hasNodeMentions(node) ||
        hasNodesMentions(nodes, checkForMentionOnOtherNodes),
      hasBeenCarried: nodes.some(({ carryForward = [] }) =>
        carryForward.some(({ questionRefId }) => questionRefId === nodeRefId),
      ),
      hasBeenUsedInLoopAndMerge: isQuestionUsedInLoopAndMerge(nodeRefId, nodes),
    };
  }, [nodes, nodesWithLogic, nodeRefId, questionQuotas]);

  const actionPayload = useMemo(
    () => ({
      nodeRefId,
      isNodeHidden,
      hasMentions,
    }),
    [nodeRefId, isNodeHidden, hasMentions],
  );

  const featuresEnabled = useMemo(() => {
    const features = [];

    if (hasLogic) {
      features.push(messages.logicRules);
    }
    if (hasMentions) {
      features.push(questionnaireMessages.recallInformation);
    }
    if (hasBeenCarried) {
      features.push(messages.carryForward);
    }
    if (hasBeenUsedInLoopAndMerge) {
      features.push(questionnaireMessages.groupLoopAddMergeLabel);
    }
    if (hasQuota) {
      features.push(messages.quotas);
    }

    return features;
  }, [
    hasLogic,
    hasMentions,
    hasBeenCarried,
    hasBeenUsedInLoopAndMerge,
    hasQuota,
  ]);

  const hideDividerStyle = useMemo(() => {
    const style = { top: -2 };

    if (DIVIDER_COLORS[nodeType]) {
      style.backgroundColor = DIVIDER_COLORS[nodeType];
    }

    return style;
  }, [nodeType]);

  return {
    showModal:
      isNodeGroup ||
      hasLogic ||
      hasMentions ||
      hasBeenCarried ||
      hasBeenUsedInLoopAndMerge ||
      hasQuota,
    featuresEnabled,
    actionPayload,
    isNodeHidden,
    canUnconditionalHideNode,
    hideDividerStyle,
    isNodeGroup,
    onToggleNodeVisibility: useCallback(
      (payload) => {
        dispatch(toggleNodeVisibility(payload));
      },
      [dispatch],
    ),
  };
}

export default useStatePropsAndActions;
